/**
 * Tenant configuration schema (PROD-568).
 *
 * Captures the per-tenant divergence levers from the master prompt v3.3
 * Pre-flight Decisions Multi-Clinic Divergence Mechanism:
 *   1. Different LLM system prompt (clinic-specific reasoning instructions)
 *   2. Different source library (Profile A: ACC/AHA + ESC + ADA; Profile B:
 *      IFM + EAS + AACE longevity)
 *   3. Different clinical priority weighting (Profile A: operational
 *      efficiency; Profile B: clinical depth)
 *
 * Phase 1 surfaces these as fixture values so the tenant switcher proves
 * the platform thesis (same patient, different intelligence). Phase 2 wires
 * them into the LLM prompt builder + RAG retriever.
 *
 * Per HC1 + HC5: zero em dashes in any string.
 * Per HC6: every config validates against `tenantConfigSchema` at load.
 */
import { z } from 'zod';
import { tenantIdSchema, type TenantId } from '../synthetic-patients/types';
import { pathwaySchema, type Pathway } from '../synthetic-patients/types';

/**
 * Clinical priority weighting (master Pre-flight Multi-Clinic Divergence).
 *
 *   - `operational-efficiency` - throughput-first reasoning (Apex)
 *   - `clinical-depth` - root-cause + multi-modal workup (Longevity)
 */
export const clinicalPriorityWeightingSchema = z.enum([
  'operational-efficiency',
  'clinical-depth',
]);
export type ClinicalPriorityWeighting = z.infer<
  typeof clinicalPriorityWeightingSchema
>;

/**
 * NBA frequency tier (Phase 1 placeholder; Phase 2 wires into the NBA
 * generation cadence).
 *
 *   - `moderate` - one NBA per step transition (Apex default cadence)
 *   - `higher` - one NBA per step transition plus opportunistic suggestions
 *     at idle (Longevity default cadence)
 */
export const nbaFrequencySchema = z.enum(['moderate', 'higher']);
export type NbaFrequency = z.infer<typeof nbaFrequencySchema>;

export const tenantConfigSchema = z.object({
  /** Tenant id; matches `useTenantStore.tenantId`. */
  id: tenantIdSchema,
  /** Display name for the switcher trigger and footer. */
  displayName: z.string().min(1),
  /** Default pathway surfaced when the tenant is loaded with no patient. */
  defaultProtocol: pathwaySchema,
  /**
   * Source library tags. Phase 2 RAG retriever filters chunks to this set
   * before the relevance threshold pass per HC8 retrieval boundaries.
   */
  sourceLibraryTags: z.array(z.string().min(1)).min(1),
  /** Clinical priority weighting per master Pre-flight divergence mechanism. */
  clinicalPriorityWeighting: clinicalPriorityWeightingSchema,
  /** NBA cadence; Phase 1 fixture, Phase 2 wires into the NBA scheduler. */
  nbaFrequency: nbaFrequencySchema,
});

export type TenantConfig = z.infer<typeof tenantConfigSchema>;

export type { TenantId, Pathway };
